import { authHeaders } from "../lib/http";
import { useState, useEffect } from "react";
import {
  ClipboardList, Plus, Trash2, Loader2, Check,
  ChevronDown, ChevronRight, GripVertical,
  Lightbulb, TrendingUp, Tag, FileText,
  Archive, Sparkles, X, Edit3, Calendar,
  AlertCircle, Clock, Flag,
} from "lucide-react";

const API_BASE = import.meta.env.VITE_API_URL || "";

// Aktif magaza (X-Shop-Id) dahil ortak basliklar
const getHeaders = () => authHeaders();

interface PlanItem {
  id: string;
  type: string;
  title: string;
  content: string | null;
  metadata: Record<string, unknown> | null;
  done: boolean;
  sortOrder: number;
}

interface Plan {
  id: string;
  title: string;
  description: string | null;
  color: string;
  status: string;
  priority: string;
  startDate: string | null;
  endDate: string | null;
  items: PlanItem[];
  createdAt: string;
  updatedAt: string;
}

const COLORS = ["#6366f1", "#ec4899", "#f59e0b", "#10b981", "#3b82f6", "#8b5cf6", "#ef4444", "#06b6d4"];

const TYPE_ICONS: Record<string, React.ElementType> = {
  idea: Lightbulb,
  trend: TrendingUp,
  keyword: Tag,
  listing: FileText,
  note: Edit3,
};

const TYPE_LABELS: Record<string, string> = {
  idea: "Fikir",
  trend: "Trend",
  keyword: "Anahtar Kelime",
  listing: "Listing",
  note: "Not",
};

const PRIORITY_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  high: { label: "Yüksek", color: "text-red-700", bg: "bg-red-100" },
  medium: { label: "Orta", color: "text-yellow-700", bg: "bg-yellow-100" },
  low: { label: "Düşük", color: "text-gray-600", bg: "bg-gray-100" },
};

function formatDate(d: string | null) {
  if (!d) return null;
  return new Date(d).toLocaleDateString("tr-TR", { day: "numeric", month: "short", year: "numeric" });
}

function daysUntil(d: string | null) {
  if (!d) return null;
  const diff = Math.ceil((new Date(d).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  return diff;
}

export default function PlansPage() {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const [newTitle, setNewTitle] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newColor, setNewColor] = useState(COLORS[0]);
  const [newPriority, setNewPriority] = useState("medium");
  const [newStartDate, setNewStartDate] = useState("");
  const [newEndDate, setNewEndDate] = useState("");
  const [newItems, setNewItems] = useState<Array<{ type: string; title: string; content: string }>>([]);
  const [newItemInput, setNewItemInput] = useState("");
  const [newItemTypeSelect, setNewItemTypeSelect] = useState("note");
  const [expandedPlan, setExpandedPlan] = useState<string | null>(null);
  const [addingItem, setAddingItem] = useState<string | null>(null);
  const [newItemTitle, setNewItemTitle] = useState("");
  const [newItemType, setNewItemType] = useState("note");
  const [newItemContent, setNewItemContent] = useState("");
  const [filter, setFilter] = useState<"active" | "completed" | "archived">("active");

  useEffect(() => { loadPlans(); }, [filter]);

  async function loadPlans() {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/plans?status=${filter}`, { headers: getHeaders() });
      if (!res.ok) console.error("[Plans] load failed:", res.status, await res.text());
      const json = await res.json();
      setPlans(json.data || []);
    } catch (err) { console.error("[Plans] load error:", err); }
    setLoading(false);
  }

  function resetForm() {
    setNewTitle(""); setNewDesc(""); setNewColor(COLORS[0]);
    setNewPriority("medium"); setNewStartDate(""); setNewEndDate("");
    setNewItems([]); setNewItemInput(""); setNewItemTypeSelect("note");
    setShowCreate(false); setEditingPlan(null);
  }

  function openEdit(plan: Plan) {
    setEditingPlan(plan);
    setNewTitle(plan.title);
    setNewDesc(plan.description || "");
    setNewColor(plan.color);
    setNewPriority(plan.priority || "medium");
    setNewStartDate(plan.startDate ? plan.startDate.slice(0, 10) : "");
    setNewEndDate(plan.endDate ? plan.endDate.slice(0, 10) : "");
    setShowCreate(true);
  }

  async function savePlan() {
    if (!newTitle.trim()) return;
    try {
      const body = {
        title: newTitle.trim(),
        description: newDesc.trim() || null,
        color: newColor,
        priority: newPriority,
        startDate: newStartDate || null,
        endDate: newEndDate || null,
      };

      const url = editingPlan
        ? `${API_BASE}/api/plans/${editingPlan.id}`
        : `${API_BASE}/api/plans`;

      const res = await fetch(url, {
        method: editingPlan ? "PATCH" : "POST",
        headers: getHeaders(),
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const errBody = await res.text();
        console.error("[Plans] save failed:", res.status, errBody);
        alert("Plan kaydedilemedi: " + errBody);
        return;
      }

      // If creating new plan with items, add them
      if (!editingPlan && newItems.length > 0) {
        const json = await res.json();
        const planId = json.data?.id;
        if (planId) {
          for (const item of newItems) {
            await fetch(`${API_BASE}/api/plans/${planId}/items`, {
              method: "POST", headers: getHeaders(),
              body: JSON.stringify({ type: item.type, title: item.title, content: item.content || null }),
            });
          }
        }
      }

      resetForm();
      loadPlans();
    } catch (err) { console.error("[Plans] save error:", err); }
  }

  async function updatePlanStatus(id: string, status: string) {
    try {
      await fetch(`${API_BASE}/api/plans/${id}`, {
        method: "PATCH", headers: getHeaders(),
        body: JSON.stringify({ status }),
      });
      loadPlans();
    } catch { /* ignore */ }
  }

  async function deletePlan(id: string) {
    if (!confirm("Bu plan silinsin mi?")) return;
    try {
      await fetch(`${API_BASE}/api/plans/${id}`, { method: "DELETE", headers: getHeaders() });
      loadPlans();
    } catch { /* ignore */ }
  }

  async function addItem(planId: string) {
    if (!newItemTitle.trim()) return;
    try {
      await fetch(`${API_BASE}/api/plans/${planId}/items`, {
        method: "POST", headers: getHeaders(),
        body: JSON.stringify({ type: newItemType, title: newItemTitle.trim(), content: newItemContent.trim() || null }),
      });
      setNewItemTitle(""); setNewItemContent(""); setAddingItem(null);
      loadPlans();
    } catch { /* ignore */ }
  }

  async function toggleItem(planId: string, item: PlanItem) {
    try {
      await fetch(`${API_BASE}/api/plans/${planId}/items/${item.id}`, {
        method: "PATCH", headers: getHeaders(),
        body: JSON.stringify({ done: !item.done }),
      });
      setPlans(prev => prev.map(p => p.id === planId ? {
        ...p, items: p.items.map(i => i.id === item.id ? { ...i, done: !i.done } : i),
      } : p));
    } catch { /* ignore */ }
  }

  async function deleteItem(planId: string, itemId: string) {
    try {
      await fetch(`${API_BASE}/api/plans/${planId}/items/${itemId}`, { method: "DELETE", headers: getHeaders() });
      setPlans(prev => prev.map(p => p.id === planId ? { ...p, items: p.items.filter(i => i.id !== itemId) } : p));
    } catch { /* ignore */ }
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-indigo-600" /> Planlarım
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Ürün fikirlerini, trendleri ve yapılacakları planla</p>
        </div>
        <button onClick={() => { resetForm(); setShowCreate(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors">
          <Plus className="h-4 w-4" /> Yeni Plan
        </button>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 mb-5 bg-gray-100 p-1 rounded-xl w-fit">
        {(["active", "completed", "archived"] as const).map(s => (
          <button key={s} onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filter === s ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
            {s === "active" ? "Aktif" : s === "completed" ? "Tamamlanan" : "Arşiv"}
          </button>
        ))}
      </div>

      {/* Create/Edit Plan Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={resetForm}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4">{editingPlan ? "Planı Düzenle" : "Yeni Plan"}</h3>

            {/* Title */}
            <input type="text" value={newTitle} onChange={e => setNewTitle(e.target.value)}
              placeholder="Plan adı (ör: Yaz Koleksiyonu 2026)"
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm mb-3 focus:ring-2 focus:ring-indigo-200 focus:outline-none" autoFocus />

            {/* Description */}
            <textarea value={newDesc} onChange={e => setNewDesc(e.target.value)}
              placeholder="Açıklama (opsiyonel)"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mb-3 h-20 resize-none focus:ring-2 focus:ring-indigo-200 focus:outline-none" />

            {/* Priority */}
            <div className="mb-3">
              <label className="text-xs font-medium text-gray-500 mb-1.5 block">Öncelik</label>
              <div className="flex gap-2">
                {(["high", "medium", "low"] as const).map(p => (
                  <button key={p} onClick={() => setNewPriority(p)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                      newPriority === p
                        ? `${PRIORITY_CONFIG[p].bg} ${PRIORITY_CONFIG[p].color} border-current`
                        : "border-gray-200 text-gray-500 hover:bg-gray-50"
                    }`}>
                    <Flag className="h-3 w-3" />
                    {PRIORITY_CONFIG[p].label}
                  </button>
                ))}
              </div>
            </div>

            {/* Date Range */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1.5 block">
                  <Calendar className="h-3 w-3 inline mr-1" />Başlangıç Tarihi
                </label>
                <input type="date" value={newStartDate} onChange={e => setNewStartDate(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-200 focus:outline-none" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1.5 block">
                  <Calendar className="h-3 w-3 inline mr-1" />Bitiş Tarihi
                </label>
                <input type="date" value={newEndDate} onChange={e => setNewEndDate(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-200 focus:outline-none" />
              </div>
            </div>

            {/* Plan Items (only for new plans) */}
            {!editingPlan && (
              <div className="mb-4">
                <label className="text-xs font-medium text-gray-500 mb-1.5 block">
                  Plan Öğeleri {newItems.length > 0 && <span className="text-gray-400">({newItems.length})</span>}
                </label>

                {/* Existing items */}
                {newItems.length > 0 && (
                  <div className="space-y-1 mb-2 max-h-40 overflow-y-auto">
                    {newItems.map((item, idx) => {
                      const Icon = TYPE_ICONS[item.type] || FileText;
                      return (
                        <div key={idx} className="flex items-center gap-2 px-2.5 py-1.5 bg-gray-50 rounded-lg group">
                          <Icon className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                          <span className="text-xs text-gray-700 flex-1 truncate">{item.title}</span>
                          <span className="text-[9px] text-gray-400">{TYPE_LABELS[item.type] || item.type}</span>
                          <button onClick={() => setNewItems(prev => prev.filter((_, i) => i !== idx))}
                            className="opacity-0 group-hover:opacity-100 p-0.5 hover:bg-red-100 rounded transition-all">
                            <X className="h-3 w-3 text-red-500" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Add item input */}
                <div className="flex gap-1.5">
                  <select value={newItemTypeSelect} onChange={e => setNewItemTypeSelect(e.target.value)}
                    className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs text-gray-600 bg-white focus:ring-2 focus:ring-indigo-200 focus:outline-none">
                    <option value="note">Not</option>
                    <option value="idea">Fikir</option>
                    <option value="trend">Trend</option>
                    <option value="keyword">Anahtar Kelime</option>
                    <option value="listing">Listing</option>
                  </select>
                  <input type="text" value={newItemInput} onChange={e => setNewItemInput(e.target.value)}
                    placeholder="Öğe ekle..."
                    onKeyDown={e => {
                      if (e.key === "Enter" && newItemInput.trim()) {
                        setNewItems(prev => [...prev, { type: newItemTypeSelect, title: newItemInput.trim(), content: "" }]);
                        setNewItemInput("");
                      }
                    }}
                    className="flex-1 border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs focus:ring-2 focus:ring-indigo-200 focus:outline-none" />
                  <button onClick={() => {
                    if (newItemInput.trim()) {
                      setNewItems(prev => [...prev, { type: newItemTypeSelect, title: newItemInput.trim(), content: "" }]);
                      setNewItemInput("");
                    }
                  }}
                    disabled={!newItemInput.trim()}
                    className="px-2.5 py-1.5 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 disabled:opacity-40 transition-colors">
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            )}

            {/* Color */}
            <div className="mb-4">
              <label className="text-xs font-medium text-gray-500 mb-1.5 block">Renk</label>
              <div className="flex gap-2">
                {COLORS.map(c => (
                  <button key={c} onClick={() => setNewColor(c)}
                    className={`w-7 h-7 rounded-full transition-transform ${newColor === c ? "ring-2 ring-offset-2 ring-gray-400 scale-110" : "hover:scale-110"}`}
                    style={{ backgroundColor: c }} />
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2">
              <button onClick={resetForm} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">İptal</button>
              <button onClick={savePlan} disabled={!newTitle.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                {editingPlan ? "Kaydet" : "Oluştur"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Plans List */}
      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
      ) : plans.length === 0 ? (
        <div className="text-center py-20 text-gray-400">
          <ClipboardList className="h-10 w-10 mx-auto mb-2 opacity-40" />
          <p className="text-sm">{filter === "active" ? "Henüz plan oluşturulmadı" : "Bu kategoride plan yok"}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {plans.map(plan => {
            const isExpanded = expandedPlan === plan.id;
            const doneCount = plan.items.filter(i => i.done).length;
            const progress = plan.items.length > 0 ? Math.round((doneCount / plan.items.length) * 100) : 0;
            const prio = PRIORITY_CONFIG[plan.priority] || PRIORITY_CONFIG.medium;
            const endDays = daysUntil(plan.endDate);
            const isOverdue = endDays !== null && endDays < 0;
            const isUrgent = endDays !== null && endDays >= 0 && endDays <= 3;

            return (
              <div key={plan.id} className={`bg-white border rounded-xl overflow-hidden ${isOverdue ? "border-red-300" : "border-gray-200"}`}>
                {/* Plan Header */}
                <div className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50/50 transition-colors"
                  onClick={() => setExpandedPlan(isExpanded ? null : plan.id)}>
                  <div className="w-2 h-10 rounded-full flex-shrink-0" style={{ backgroundColor: plan.color }} />
                  <button className="p-0.5">
                    {isExpanded ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-semibold text-gray-900">{plan.title}</h3>
                      <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-medium ${prio.bg} ${prio.color}`}>
                        {prio.label}
                      </span>
                    </div>
                    {plan.description && <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{plan.description}</p>}
                    {/* Date info */}
                    {(plan.startDate || plan.endDate) && (
                      <div className="flex items-center gap-2 mt-1">
                        <Calendar className="h-3 w-3 text-gray-400" />
                        <span className="text-[10px] text-gray-400">
                          {plan.startDate ? formatDate(plan.startDate) : "—"}
                          {" → "}
                          {plan.endDate ? formatDate(plan.endDate) : "—"}
                        </span>
                        {isOverdue && (
                          <span className="text-[9px] font-medium text-red-600 bg-red-50 px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                            <AlertCircle className="h-2.5 w-2.5" /> Süresi geçti
                          </span>
                        )}
                        {isUrgent && !isOverdue && (
                          <span className="text-[9px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                            <Clock className="h-2.5 w-2.5" /> {endDays} gün kaldı
                          </span>
                        )}
                        {endDays !== null && endDays > 3 && (
                          <span className="text-[9px] text-gray-400">{endDays} gün kaldı</span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    {plan.items.length > 0 && (
                      <div className="flex items-center gap-2">
                        <div className="w-20 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all" style={{ width: `${progress}%`, backgroundColor: plan.color }} />
                        </div>
                        <span className="text-[10px] text-gray-400 w-12 text-right">{doneCount}/{plan.items.length}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                      <button onClick={() => openEdit(plan)}
                        title="Düzenle"
                        className="p-1.5 rounded-lg hover:bg-blue-50 text-gray-400 hover:text-blue-600 transition-colors">
                        <Edit3 className="h-3.5 w-3.5" />
                      </button>
                      {filter === "active" && (
                        <button onClick={() => updatePlanStatus(plan.id, "completed")}
                          title="Tamamlandı"
                          className="p-1.5 rounded-lg hover:bg-emerald-50 text-gray-400 hover:text-emerald-600 transition-colors">
                          <Check className="h-3.5 w-3.5" />
                        </button>
                      )}
                      <button onClick={() => updatePlanStatus(plan.id, filter === "archived" ? "active" : "archived")}
                        title={filter === "archived" ? "Aktif Yap" : "Arşivle"}
                        className="p-1.5 rounded-lg hover:bg-orange-50 text-gray-400 hover:text-orange-500 transition-colors">
                        <Archive className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => deletePlan(plan.id)}
                        className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Expanded Items */}
                {isExpanded && (
                  <div className="border-t border-gray-100 px-4 py-3">
                    {plan.items.length === 0 && !addingItem && (
                      <p className="text-xs text-gray-400 text-center py-4">Bu plana henüz öğe eklenmedi</p>
                    )}

                    {/* Items list */}
                    <div className="space-y-1">
                      {plan.items.map(item => {
                        const Icon = TYPE_ICONS[item.type] || Edit3;
                        return (
                          <div key={item.id} className={`flex items-start gap-2 px-2 py-1.5 rounded-lg group hover:bg-gray-50 transition-colors ${item.done ? "opacity-50" : ""}`}>
                            <GripVertical className="h-4 w-4 text-gray-200 mt-0.5 flex-shrink-0 cursor-grab" />
                            <button onClick={() => toggleItem(plan.id, item)}
                              className={`mt-0.5 flex-shrink-0 w-4 h-4 rounded border ${item.done ? "bg-indigo-600 border-indigo-600" : "border-gray-300 hover:border-indigo-400"} flex items-center justify-center transition-colors`}>
                              {item.done && <Check className="h-3 w-3 text-white" />}
                            </button>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <Icon className="h-3 w-3 text-gray-400 flex-shrink-0" />
                                <span className={`text-xs font-medium ${item.done ? "line-through text-gray-400" : "text-gray-800"}`}>{item.title}</span>
                                <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-400">{TYPE_LABELS[item.type] || item.type}</span>
                              </div>
                              {item.content && <p className="text-[11px] text-gray-500 mt-0.5 ml-4">{item.content}</p>}
                              {item.metadata != null && Boolean((item.metadata as Record<string, unknown>).source) && (
                                <p className="text-[9px] text-indigo-500 mt-0.5 ml-4 flex items-center gap-1">
                                  <Sparkles className="h-2.5 w-2.5" /> {String((item.metadata as Record<string, unknown>).source)}
                                </p>
                              )}
                            </div>
                            <button onClick={() => deleteItem(plan.id, item.id)}
                              className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-red-50 text-gray-400 hover:text-red-500 transition-all">
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        );
                      })}
                    </div>

                    {/* Add Item */}
                    {addingItem === plan.id ? (
                      <div className="mt-3 bg-gray-50 rounded-lg p-3">
                        <div className="flex gap-2 mb-2">
                          {Object.entries(TYPE_LABELS).map(([key, label]) => {
                            const Icon = TYPE_ICONS[key] || Edit3;
                            return (
                              <button key={key} onClick={() => setNewItemType(key)}
                                className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium transition-colors ${
                                  newItemType === key ? "bg-indigo-100 text-indigo-700" : "text-gray-500 hover:bg-gray-200"
                                }`}>
                                <Icon className="h-3 w-3" /> {label}
                              </button>
                            );
                          })}
                        </div>
                        <input type="text" value={newItemTitle} onChange={e => setNewItemTitle(e.target.value)}
                          onKeyDown={e => { if (e.key === "Enter" && newItemTitle.trim()) addItem(plan.id); }}
                          placeholder="Başlık..."
                          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs mb-2 focus:ring-2 focus:ring-indigo-200 focus:outline-none" autoFocus />
                        <textarea value={newItemContent} onChange={e => setNewItemContent(e.target.value)}
                          placeholder="Detay (opsiyonel)"
                          className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-xs mb-2 h-14 resize-none focus:ring-2 focus:ring-indigo-200 focus:outline-none" />
                        <div className="flex justify-end gap-2">
                          <button onClick={() => { setAddingItem(null); setNewItemTitle(""); setNewItemContent(""); }}
                            className="px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-200 rounded-lg">İptal</button>
                          <button onClick={() => addItem(plan.id)} disabled={!newItemTitle.trim()}
                            className="px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50">Ekle</button>
                        </div>
                      </div>
                    ) : (
                      <button onClick={() => setAddingItem(plan.id)}
                        className="mt-2 flex items-center gap-1.5 text-xs text-indigo-600 hover:text-indigo-700 font-medium px-2 py-1.5 rounded-lg hover:bg-indigo-50 transition-colors">
                        <Plus className="h-3.5 w-3.5" /> Öğe Ekle
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
